import React, { useState, useMemo, useEffect, type JSX } from "react";
import { SupSlider, SupText } from "@repo/core-ui";
import { twJoin } from "tailwind-merge";
import { AmountInput } from "./components/Input";
import type { Balance, Chain, Config } from "./types";


export interface AmountInputProps {
  value?: string;
  className?: string;
  inputClassName?: string;
  label?: string | JSX.Element;
  balance: Balance;
  chain: Chain;
  config: Config;
  sliderMarks?: number | number[] | false;
  onChange?: (value?: string) => void;
  onSwitchToken?: () => void;
  disableFiat?: boolean;
  disableTokenChange?: boolean;
}

interface AmountState {
  crypto: string;
  fiat: string;
  percent: number;
}

export function SupAmountInput({
  value: incomeValue = "",
  className,
  inputClassName,
  label,
  balance,
  chain,
  config,
  sliderMarks = 25,
  onChange,
  onSwitchToken,
  disableFiat,
  disableTokenChange,
}: AmountInputProps) {
  const [type, setType] = useState<"fiat" | "token">("token");
  const [value, setValue] = useState<AmountState>({ crypto: "", fiat: "", percent: 0 });
  const fiatBalance = useMemo(() => `$${(parseFloat(balance.amount) * balance.price).toFixed(config.fiatMaxDecimals)}`, []);

  const token = {
    icon: balance.icon ?? balance.logoURI ?? "",
    name: balance.symbol,
    chainName: chain.name,
    chainIcon: chain.icon,
  };

  useEffect(() => {
    setValue(prevValue => {
      if (prevValue.crypto !== incomeValue) {
        const parsedValue = parseFloat(incomeValue);

        if (isNaN(parsedValue) || parsedValue === 0) {
          return {
            crypto: incomeValue,
            fiat: "",
            percent: 0,
          };
        }

        return {
          crypto: incomeValue,
          fiat: (parsedValue * balance.price).toFixed(config.fiatMaxDecimals),
          percent: Math.round((parsedValue * 100) / parseFloat(balance.amount)),
        };
      }

      return prevValue;
    });
  }, [incomeValue, balance.price, config.fiatMaxDecimals, balance.amount]);

  function handleTypeSwitch() {
    if (disableFiat) return;
    setType(type => (type === "fiat" ? "token" : "fiat"));
  }

  function handleAmountChange(amount: string) {
    const parsedValue = parseFloat(amount);

    if (isNaN(parsedValue) || parsedValue === 0) {
      setValue({ crypto: amount, fiat: "", percent: 0 });
      onChange?.(amount);
      return;
    }

    setValue({
      crypto: amount,
      fiat: (parsedValue * balance.price).toFixed(config.fiatMaxDecimals),
      percent: Math.round((parsedValue * 100) / parseFloat(balance.amount)),
    });
    onChange?.(amount);
  }

  function handleTotalPriceChange(price: string) {
    const parsedValue = parseFloat(price);
    const parsedBalance = parseFloat(balance.amount);

    if (isNaN(parsedValue) || parsedValue === 0) {
      setValue({ crypto: "", fiat: price, percent: 0 });
      onChange?.("");
      return;
    }

    const amount = (parsedValue / balance.price).toFixed(balance.decimals);

    setValue({ crypto: amount, fiat: price, percent: Math.round((parsedValue * 100) / (parsedBalance * balance.price)) });
    onChange?.(amount);
  }

  function handlePercentChange(percent: number | number[]) {
    if (Array.isArray(percent)) return;

    const parsedBalance = parseFloat(balance.amount);
    const fiatBalance = parsedBalance * balance.price;

    if (percent === 0) {
      setValue({ crypto: "", fiat: "", percent: 0 });
      return "";
    }

    if (percent === 100) {
      setValue({ crypto: balance.amount, fiat: fiatBalance.toFixed(config.fiatMaxDecimals), percent: 100 });
      return balance.amount;
    }

    const amount = ((parsedBalance * percent) / 100).toFixed(balance.decimals);

    setValue({ crypto: amount, fiat: ((fiatBalance * percent) / 100).toFixed(config.fiatMaxDecimals), percent });
    return amount;
  }

  function handleAfterPercentChange(percent: number | number[]) {
    const amount = handlePercentChange(percent);
    onChange?.(amount);
  }

  return (
    <div className={twJoin("sup-amount-input", className)}>
      <SupText variant="caption" className="mb-3">
        {label}
      </SupText>
      {type === "fiat" ? (
        <AmountInput
          disableSwitch={disableFiat}
          disableTokenSwitch={disableTokenChange}
          className={twJoin("border-b-0", inputClassName)}
          placeholder="$0"
          prefix="$"
          value={value.fiat}
          hint={value.crypto}
          hintPlaceholder="0.0"
          balance={fiatBalance}
          token={token}
          onChange={handleTotalPriceChange}
          onSwitch={handleTypeSwitch}
          onSwitchToken={onSwitchToken}
        />
      ) : (
        <AmountInput
          disableSwitch={disableFiat}
          disableTokenSwitch={disableTokenChange}
          className={twJoin("border-b-0", inputClassName)}
          placeholder="0"
          value={value.crypto}
          hint={value.fiat ? `$${value.fiat}` : ""}
          hintPlaceholder="$0"
          balance={parseFloat(balance.amount).toFixed(config.tokenMaxDecimals)}
          token={token}
          onChange={handleAmountChange}
          onSwitch={handleTypeSwitch}
          onSwitchToken={onSwitchToken}
        />
      )}
      <SupSlider value={value.percent} min={0} max={100} step={1} grabberType="value" marks={sliderMarks} onChange={handlePercentChange} onAfterChange={handleAfterPercentChange} />
    </div>
  );
}
